import { useState, useEffect, useCallback } from 'react'
import { fetchWithAuth } from '@/lib/api'

/** Fired after PUT /api/organizations/:orgId/extensions/:id so other hook instances (e.g. sidebar) refetch. */
export const WORKSPACE_EXTENSIONS_CHANGED = 'workspace-extensions-changed'

interface WorkspaceExtension {
  extension_id: string
  enabled: boolean
  config: Record<string, any>
}

export function useWorkspaceExtensions(organizationId: string | null | undefined) {
  const [extensions, setExtensions] = useState<WorkspaceExtension[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchExtensions = useCallback(async () => {
    if (!organizationId) {
      setExtensions([])
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await fetchWithAuth(
        `/api/organizations/${organizationId}/extensions`
      )
      if (response.ok) {
        const data = await response.json()
        setExtensions(data)
      } else {
        setExtensions([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load extensions')
      setExtensions([])
    } finally {
      setIsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    fetchExtensions()
  }, [fetchExtensions])

  // Settings and other panels toggle extensions via their own hook instance; sidebar uses another.
  useEffect(() => {
    if (!organizationId) return
    const handler = (event: Event) => {
      const id = (event as CustomEvent<{ organizationId?: string }>).detail?.organizationId
      if (id === organizationId) void fetchExtensions()
    }
    window.addEventListener(WORKSPACE_EXTENSIONS_CHANGED, handler)
    return () => window.removeEventListener(WORKSPACE_EXTENSIONS_CHANGED, handler)
  }, [organizationId, fetchExtensions])

  const isExtensionEnabled = useCallback(
    (extensionId: string): boolean => {
      // Fail-closed while loading so disabled extensions (e.g. Basecamp on app-product
      // workspace) do not flash in the browse sidebar before GET .../extensions returns.
      if (isLoading && extensions.length === 0) return false
      const ext = extensions.find(e => e.extension_id === extensionId)
      if (!ext) return false
      return ext.enabled
    },
    [extensions, isLoading]
  )

  const toggleExtension = useCallback(
    async (extensionId: string, enabled: boolean) => {
      if (!organizationId) return
      const res = await fetchWithAuth(
        `/api/organizations/${organizationId}/extensions/${extensionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        }
      )
      if (res.ok) {
        await fetchExtensions()
        window.dispatchEvent(
          new CustomEvent(WORKSPACE_EXTENSIONS_CHANGED, { detail: { organizationId } })
        )
      } else {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to update extension')
      }
    },
    [organizationId, fetchExtensions]
  )

  const updateExtensionConfig = useCallback(
    async (extensionId: string, config: Record<string, any>) => {
      if (!organizationId) return
      const ext = extensions.find(e => e.extension_id === extensionId)
      const res = await fetchWithAuth(
        `/api/organizations/${organizationId}/extensions/${extensionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: ext?.enabled ?? true, config }),
        }
      )
      if (res.ok) {
        await fetchExtensions()
        window.dispatchEvent(
          new CustomEvent(WORKSPACE_EXTENSIONS_CHANGED, { detail: { organizationId } })
        )
      } else {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to update extension config')
      }
    },
    [organizationId, extensions, fetchExtensions]
  )

  return { extensions, isLoading, error, isExtensionEnabled, toggleExtension, updateExtensionConfig, refetch: fetchExtensions }
}
